import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TipJarCopy } from '@shared/dream-components';

export const useTipJarCopy = (publicationName: string): TipJarCopy => {
  const { t } = useTranslation();

  return useMemo<TipJarCopy>(
    () => ({
      jarName: t('tip_jar.jar_name'),
      ofGoal: (goal) => t('tip_jar.of_goal', { goal }),
      remaining: (amount) => t('tip_jar.remaining', { amount }),
      funded: t('tip_jar.funded'),
      supporters: (count) => (count === 0 ? t('tip_jar.supporters_none') : t('tip_jar.supporters', { count })),
      chipIn: (amount) => t('tip_jar.chip_in', { amount }),
      enterAmount: t('tip_jar.enter_amount'),
      amountLabel: t('tip_jar.amount_label'),
      otherAmount: t('tip_jar.other_amount'),
      customAmountLabel: t('tip_jar.custom_amount_label'),
      amountOutOfRange: (minimum, maximum) => t('tip_jar.amount_out_of_range', { minimum, maximum }),
      addNote: t('tip_jar.add_note'),
      removeNote: t('tip_jar.remove_note'),
      noteLabel: (name) => t('tip_jar.note_label', { publicationName: name }),
      notePlaceholder: t('tip_jar.note_placeholder'),
      notePrivacy: (name) => t('tip_jar.note_privacy', { publicationName: name }),
      // Not `count`, which would send i18next looking for plural forms.
      noteCharacterCount: (current, max) => t('tip_jar.note_character_count', { current, max }),
      coverFee: (name, tipAmount, fee) => t('tip_jar.cover_fee', { publicationName: name, amount: tipAmount, fee }),
      coverFeeAmount: (feeAmount) => t('tip_jar.cover_fee_amount', { amount: feeAmount }),
      coverFeeWithoutAmount: t('tip_jar.cover_fee_without_amount'),
      changeAmount: t('tip_jar.change_amount'),
      checkoutLoading: t('tip_jar.checkout_loading'),
      secureCheckout: t('tip_jar.secure_checkout'),
      thanksHeading: t('tip_jar.thanks_heading'),
      thanksBody: t('tip_jar.thanks_body', { publicationName }),
      chipInAgain: t('tip_jar.chip_in_again'),
      shareHeading: t('tip_jar.share_heading'),
      shareBody: t('tip_jar.share_body'),
      shareText: (percentFunded) =>
        percentFunded === null
          ? t('tip_jar.share_text_no_goal', { publicationName })
          : t('tip_jar.share_text', { publicationName, percent: percentFunded }),
      share: t('tip_jar.share'),
      copyLink: t('tip_jar.copy_link'),
      linkCopied: t('tip_jar.link_copied'),
      readMore: t('tip_jar.read_more'),
      showLess: t('tip_jar.show_less'),
    }),
    [t, publicationName],
  );
};
